import { motion } from 'motion/react';
import { Check, GraduationCap, Home, Heart, Shield, Users, ArrowRight, MapPin, MessageCircle, BookOpen } from 'lucide-react';

interface ForFamiliesPathwayProps {
  onNavigateHome: () => void;
  onNavigateToSchoolSupport: () => void;
  onNavigateToRealEstate: () => void;
  onNavigateToHomeManagement: () => void;
}

export function ForFamiliesPathway({
  onNavigateHome,
  onNavigateToSchoolSupport,
  onNavigateToRealEstate,
  onNavigateToHomeManagement
}: ForFamiliesPathwayProps) {
  return (
    <div className="min-h-screen bg-white">
      {/* Section 1: Hero Header */}
      <section className="relative pt-32 pb-24 bg-gradient-to-br from-[#0A1A33] to-[#0A1A33]/90 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 rounded-full bg-[#C6A667] blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full bg-[#C6A667] blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-white mb-6 text-[48px]">Support for Families in the U.S.</h1>
              <p className="text-white/90 text-lg leading-relaxed mb-8">
                We help your family adapt, succeed, and thrive — from school choices to daily life transitions.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-4 bg-[#C6A667] hover:bg-[#D4B578] text-white rounded-full transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Free 15-min Consultation
              </motion.button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1763013259158-8a8370542ddb?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwYXJlbnQlMjBjaGlsZCUyMHNjaG9vbHxlbnwxfHx8fDE3NjUwMzU2MDh8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                  alt="Parent and child in school environment"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 2: What We Help With */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[36px]">What We Help With</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive support for every aspect of your family&apos;s life in the U.S.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: GraduationCap,
                title: 'School selection and U.S. school system navigation',
                description: 'Find the right school match and navigate applications with confidence'
              },
              {
                icon: Users,
                title: 'Family support through relocation and cultural transition',
                description: 'Smooth adaptation to American life while honoring your cultural values'
              },
              {
                icon: MessageCircle,
                title: 'Guidance for challenges such as bullying, communication, or behavior concerns',
                description: 'Professional support for sensitive school and family situations'
              },
              {
                icon: Home,
                title: 'Home setup and local life integration',
                description: 'Complete support getting your home and family established'
              },
              {
                icon: Heart,
                title: 'Bilingual communication support',
                description: 'Seamless coordination in both English and Chinese'
              }
            ].map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
                >
                  <div className="w-14 h-14 bg-[#C6A667]/10 rounded-xl flex items-center justify-center mb-4">
                    <IconComponent className="w-7 h-7 text-[#C6A667]" />
                  </div>
                  <h3 className="text-[#0A1A33] mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 3: Recommended Service Bundle */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[36px]">Recommended Service Bundle</h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-2">
              The essential services most families need when settling in the U.S.
            </p>
            <p className="text-sm text-[#0A1A33]/60 italic">
              Recommended for most families new to the U.S.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'School & Family Support',
                description: 'Navigate school choices, applications, and family transitions with expert guidance.',
                icon: GraduationCap,
                onClick: onNavigateToSchoolSupport
              },
              {
                title: 'Real Estate & Relocation',
                description: 'Find the perfect home in the right neighborhood for your family.',
                icon: MapPin,
                onClick: onNavigateToRealEstate
              },
              {
                title: 'Home Management',
                description: 'Keep your home running smoothly with vendor coordination and maintenance support.',
                icon: Home,
                onClick: onNavigateToHomeManagement
              }
            ].map((service, index) => {
              const IconComponent = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  whileHover={{ y: -5 }}
                  className="group bg-gray-50 rounded-xl p-8 border-2 border-transparent hover:border-[#C6A667] transition-all duration-300 cursor-pointer"
                  onClick={service.onClick}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#0A1A33] to-[#0A1A33]/80 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent className="w-8 h-8 text-[#C6A667]" />
                  </div>
                  <h3 className="text-[#0A1A33] mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <button 
                    className="group/btn inline-flex items-center text-[#C6A667] hover:text-[#D4B578] transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      service.onClick();
                    }}
                  >
                    <span>Explore Service</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 4: Example Scenarios */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[36px]">Common Family Scenarios</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              See how we&apos;ve helped families like yours
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: GraduationCap,
                title: 'Moving to the U.S. mid-year',
                description: 'Your family is relocating in February. We help find schools accepting mid-year transfers, coordinate the move, and set up your home.'
              },
              {
                icon: BookOpen,
                title: 'Choosing between public and private schools',
                description: 'You\'re unsure which school system fits your child. We analyze options, tour schools with you, and guide the application process.'
              },
              {
                icon: Shield,
                title: 'Making sure your home stays safe while you travel',
                description: 'Your family travels frequently. We provide regular home checks, maintenance coordination, and ensure everything is ready when you return.'
              }
            ].map((scenario, index) => {
              const IconComponent = scenario.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="bg-white p-8 rounded-xl shadow-lg"
                >
                  <div className="w-14 h-14 bg-[#C6A667]/10 rounded-full flex items-center justify-center mb-6">
                    <IconComponent className="w-7 h-7 text-[#C6A667]" />
                  </div>
                  <h3 className="text-[#0A1A33] mb-3">{scenario.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{scenario.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 5: How It Works (Short) */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[36px]">How It Works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A simple, proven process to support your family
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                number: '01',
                title: 'Free Consultation',
                description: 'Share your family\'s needs in a 15-minute call'
              },
              {
                number: '02',
                title: 'Custom Proposal',
                description: 'Receive a clear plan tailored to your situation'
              },
              {
                number: '03',
                title: 'We Execute',
                description: 'Sit back while we coordinate and handle details'
              },
              {
                number: '04',
                title: 'You Thrive',
                description: 'Focus on what matters while we handle the rest'
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="relative"
              >
                <div className="text-[#C6A667]/20 mb-4 text-[64px] leading-none">{step.number}</div>
                <h3 className="text-[#0A1A33] mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-12 -right-4 w-8 h-px bg-[#C6A667]/30" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 6: Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[36px]">What Families Say</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                quote: "They made our transition to the U.S. so much easier. From finding the right school to setting up our home, everything was handled professionally.",
                author: "Parent of two",
                location: "Bay Area, CA"
              },
              {
                quote: "The bilingual support was invaluable. They helped us communicate with our child's school and navigate situations we didn't understand.",
                author: "Family relocating from Shanghai",
                location: "Seattle, WA"
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="bg-white p-8 rounded-xl shadow-lg"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Check key={i} className="w-5 h-5 text-[#C6A667]" />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed mb-6 italic">
                  &quot;{testimonial.quote}&quot;
                </p>
                <div className="text-sm">
                  <div className="text-[#0A1A33]">{testimonial.author}</div>
                  <div className="text-gray-500">{testimonial.location}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7: Final CTA */}
      <section className="py-24 bg-gradient-to-br from-[#0A1A33] via-[#0D2347] to-[#0A1A33]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-white mb-8 text-[40px]">
              Let&apos;s make your family&apos;s life in the U.S. easier.
            </h2>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="group px-10 py-4 bg-[#C6A667] hover:bg-[#D4B578] text-white rounded-full transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center gap-3"
            >
              <span>Book Free 15-min Consultation</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
